import { useCallback } from 'react';
import { useAppNavigation, type NavigationView } from './useAppNavigation';
import { usePatientStore } from '../stores/usePatientStore';
import { useMedicalRecordStore } from '../stores/useMedicalRecordStore';
import type { AIAction } from '../types/aiActions';

interface ExecutionResult {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Hook to execute AI actions
 */
export const useAIActionExecutor = () => {
  const { navigateTo } = useAppNavigation();
  const { patients } = usePatientStore();
  const { medicalRecords } = useMedicalRecordStore();

  /**
   * Execute a single AI action
   */
  const executeAction = useCallback(async (action: AIAction): Promise<ExecutionResult> => {
    console.log('Executing AI action:', action);

    try {
      switch (action.type) {
        case 'navigate':
          return await handleNavigate(action);

        case 'search_patients':
          return await handleSearchPatients(action);

        case 'search_records':
          return await handleSearchRecords(action);

        case 'open_patient':
          return await handleOpenPatient(action);

        case 'open_record':
          return await handleOpenRecord(action);

        case 'create_appointment':
          return await handleCreateAppointment(action);

        case 'create_record':
          return await handleCreateRecord(action);

        case 'answer_question':
          return {
            success: true,
            message: action.parameters.answer as string
          };

        case 'search_appointments':
          return await handleSearchAppointments(action);

        case 'search_lab_orders':
          return await handleSearchLabOrders(action);

        case 'prepare_booking':
          // Booking proposals are rendered inline via BookingConfirmationCard.
          // The actual appointment creation is handled by the card's Confirm button.
          return {
            success: true,
            message: 'Booking proposal presented to user for confirmation'
          };

        case 'check_provider_availability':
        case 'find_available_slots':
          // These are informational actions - the response message contains the data
          return {
            success: true,
            message: 'Availability information provided'
          };

        case 'select_patient':
          // This action requires user confirmation, so we just mark it as successful
          // The actual selection will be handled by the UI
          return {
            success: true,
            message: 'Please select a patient from the list'
          };

        case 'open_lab_order':
          return await handleOpenLabOrder(action);

        case 'open_appointment':
          return await handleOpenAppointment(action);

        case 'open_medical_record':
          return await handleOpenMedicalRecord(action);

        case 'get_patient_medical_records':
          return await handleGetPatientMedicalRecords(action);

        case 'get_patient_lab_orders':
          return await handleGetPatientLabOrders(action);

        case 'get_patient_schedules':
          return await handleGetPatientSchedules(action);

        case 'select_lab_order':
        case 'select_appointment':
        case 'select_medical_record':
        case 'select_patient_for_lab_orders':
        case 'select_patient_for_appointments':
        case 'select_patient_for_medical_records':
          // These actions require user selection from a list
          // The actual selection will be handled by the UI
          return {
            success: true,
            message: 'Please make a selection from the list'
          };

        default:
          return {
            success: false,
            error: `Unknown action type: ${action.type}`
          };
      }
    } catch (error) {
      console.error('Error executing action:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }, [navigateTo, patients, medicalRecords]);

  /**
   * Execute multiple actions in sequence
   */
  const executeActions = useCallback(async (actions: AIAction[]): Promise<ExecutionResult[]> => {
    const results: ExecutionResult[] = [];

    for (const action of actions) {
      const result = await executeAction(action);
      results.push(result);

      // Stop if an action fails
      if (!result.success) {
        break;
      }
    }

    return results;
  }, [executeAction]);

  /**
   * Navigate to a page
   */
  const handleNavigate = async (action: AIAction): Promise<ExecutionResult> => {
    const { view, ...params } = action.parameters as { view: NavigationView } & Record<string, string>;

    // Pass all parameters except 'view' to navigateTo
    navigateTo(view, params);

    return {
      success: true,
      message: `Navigated to ${view}`
    };
  };

  /**
   * Search for patients
   */
  const handleSearchPatients = async (action: AIAction): Promise<ExecutionResult> => {
    const { query } = action.parameters as Record<string, string>;

    // Navigate to patients page with search query
    navigateTo('patients', { search: query });

    return {
      success: true,
      message: `Searching for patients: "${query}"`
    };
  };

  /**
   * Search for medical records
   */
  const handleSearchRecords = async (action: AIAction): Promise<ExecutionResult> => {
    const { query, patientId } = action.parameters as Record<string, string>;

    // Navigate to records page with search parameters
    navigateTo('records', { search: query, patientId });

    return {
      success: true,
      message: `Searching for records: "${query}"`
    };
  };

  /**
   * Open a patient's details
   */
  const handleOpenPatient = async (action: AIAction): Promise<ExecutionResult> => {
    const { patientId } = action.parameters as Record<string, string>;

    navigateTo('patient-details', { patientId });

    return {
      success: true,
      message: `Opening patient ${patientId}`
    };
  };

  /**
   * Open a medical record
   */
  const handleOpenRecord = async (action: AIAction): Promise<ExecutionResult> => {
    const { recordId } = action.parameters as Record<string, string>;

    navigateTo('record-details', { recordId });

    return {
      success: true,
      message: `Opening record ${recordId}`
    };
  };

  /**
   * Create a new appointment
   */
  const handleCreateAppointment = async (action: AIAction): Promise<ExecutionResult> => {
    const { patientId, date, reason } = action.parameters as Record<string, string>;

    // Navigate to create appointment page with pre-filled data
    navigateTo('add-appointment', { patientId, date, reason });

    return {
      success: true,
      message: 'Opening create appointment form'
    };
  };

  /**
   * Create a new medical record
   */
  const handleCreateRecord = async (action: AIAction): Promise<ExecutionResult> => {
    const { patientId } = action.parameters as Record<string, string>;

    // Navigate to create record page
    navigateTo('add-record', { patientId });

    return {
      success: true,
      message: 'Opening create medical record form'
    };
  };

  /**
   * Search appointments
   */
  const handleSearchAppointments = async (action: AIAction): Promise<ExecutionResult> => {
    const { query, patientId, date } = action.parameters as Record<string, string>;

    // Navigate to schedule with filters
    navigateTo('schedule', { search: query, patientId, date });

    return {
      success: true,
      message: 'Searching appointments'
    };
  };

  /**
   * Search lab orders
   */
  const handleSearchLabOrders = async (action: AIAction): Promise<ExecutionResult> => {
    const { query, patientId } = action.parameters as Record<string, string>;

    // Navigate to lab orders with filters
    navigateTo('lab-orders', { search: query, patientId });

    return {
      success: true,
      message: 'Searching lab orders'
    };
  };

  /**
   * Open a specific lab order
   */
  const handleOpenLabOrder = async (action: AIAction): Promise<ExecutionResult> => {
    const { labOrderId, patientId } = action.parameters as Record<string, string>;

    // Navigate to lab order details - route expects 'orderId' parameter
    navigateTo('lab-order-details', { orderId: labOrderId, patientId });

    return {
      success: true,
      message: `Opening lab order ${labOrderId}`
    };
  };

  /**
   * Open a specific appointment
   */
  const handleOpenAppointment = async (action: AIAction): Promise<ExecutionResult> => {
    const { appointmentId, patientId } = action.parameters as Record<string, string>;

    // Navigate to appointment details
    navigateTo('appointment-details', { appointmentId, patientId });

    return {
      success: true,
      message: `Opening appointment ${appointmentId}`
    };
  };

  /**
   * Open a specific medical record
   */
  const handleOpenMedicalRecord = async (action: AIAction): Promise<ExecutionResult> => {
    const { medicalRecordId, patientId } = action.parameters as Record<string, string>;

    // Navigate to medical record details - route expects 'recordId' parameter
    navigateTo('record-details', { recordId: medicalRecordId, patientId });

    return {
      success: true,
      message: `Opening medical record ${medicalRecordId}`
    };
  };

  /**
   * Get all medical records for a patient
   */
  const handleGetPatientMedicalRecords = async (action: AIAction): Promise<ExecutionResult> => {
    const { patientId, patientName } = action.parameters as Record<string, string>;

    // Navigate to records page filtered by patient
    navigateTo('records', { patientId, patientName });

    return {
      success: true,
      message: `Viewing medical records for ${patientName || 'patient'}`
    };
  };

  /**
   * Get all lab orders for a patient
   */
  const handleGetPatientLabOrders = async (action: AIAction): Promise<ExecutionResult> => {
    const { patientId, patientName } = action.parameters as Record<string, string>;

    // Navigate to lab orders page filtered by patient
    navigateTo('lab-orders', { patientId, patientName });

    return {
      success: true,
      message: `Viewing lab orders for ${patientName || 'patient'}`
    };
  };

  /**
   * Get all appointments/schedules for a patient
   */
  const handleGetPatientSchedules = async (action: AIAction): Promise<ExecutionResult> => {
    const { patientId, patientName } = action.parameters as Record<string, string>;

    // Navigate to schedule page filtered by patient
    navigateTo('schedule', { patientId, patientName });

    return {
      success: true,
      message: `Viewing appointments for ${patientName || 'patient'}`
    };
  };

  return {
    executeAction,
    executeActions
  };
};
